

/**
 * @name AvailabilityController
 * @description Controller for availability
 * @param {type} Artists Description
 * @param {type} SchedulingFactory Description
 * @param {type} CheckCollisions Description
 * @param {type} ScheduleUtilFactory Description
 * @param {type} moment Description
 * @param {type} Session Description
 * @param {type} Appointments Description
 * @param {type} ShopHours Description
 * @param {type} Schedule Description
 * @param {type} CalculateAvailability Description
 * @param {type} $q Description
 * @param {type} _ Description
 */
function AvailabilityController(
  Artists,
  SchedulingFactory,
  CheckCollisions,
  ScheduleUtilFactory,
  moment,
  Session,
  Appointments,
  ShopHours,
  Schedule,
  CalculateAvailability,
  $q,
  _
) {
  let vm = this;
  vm.type = _.isUndefined(vm.type) ? 'full' : vm.type;

  // Exposed Functions
  vm.selectArtist = selectArtist;
  vm.updateSearch = updateSearch;
  vm.getAvailability = getAvailability;
  vm.getSchedule = getSchedule;
  vm.detectCollision = detectCollision;
  vm.pagerPrev = pagerPrev;
  vm.pagerNext = pagerNext;
  vm.updateTime = updateTime;
  vm.reset = reset;
  vm.appointmentCompare = _.clone(vm.appointment);

  // Defaults
  vm.session = Session;
  vm.searchType = 1;
  vm.firstRun = true;
  vm.accordions = {};
  vm.range = {
    minDate: moment().toDate(),
    startDate: moment().toDate()
  };
  vm.time = {
    hours: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    minutes: [0, 15, 30, 45]
  };

  if (_.isUndefined(vm.appointment)) {
    vm.appointment = {};
  }

  // Block Data
  vm.availability = {};
  vm.selected = {};

  // Pager
  vm.resultLimit = 5;
  vm.pagerStart = 0;
  vm.pagerEnd = vm.resultLimit;

  // If you're an operator, get the full list of active artists at your shop.
  if (vm.session.activeState.role === 'operator') {
    Artists.refreshArtists().then(function(artists) {
      vm.artists = artists.confirmed;
    });
  }

  if (vm.appointment.appointment_datetime) {
    vm.accordions.openSchedule = false;
  } else {
    vm.accordions.openSchedule = true;
  }

  /**
   * @name getAvailability
   * @description Get artist time slots
   * @return {object} Unknown.
   */
  function getAvailability() {
    vm.accordions.openSchedule = true;
    vm.loading = true;
    let defaultSearch = (vm.searchType === 1);
    let processedDates = SchedulingFactory.preprocessAvailabilityLookup(
      vm.range,
      defaultSearch
    );
    let duration = ScheduleUtilFactory.calculateDuration(
      vm.appointment.durationHours,
      vm.appointment.durationMinutes
    );
    let search = false;
    let timestamp;
    let promise1 = ShopHours.get(Session.activeState.association.shop.id)
    .then(function(hours) {
      let data = {};
      let shopHours = ScheduleUtilFactory.getShopTimes(
        hours.all,
        processedDates.start,
        processedDates.end
      );
      return Appointments.get(
        search,
        shopHours.earliest.start,
        shopHours.latest.end,
        timestamp,
        vm.appointment.artist.id)
      .then(function(appointments) {
        data = {
          shopHours: shopHours,
          appointments: appointments
        };
        return data;
      });
    });

    let promise2 = Schedule.fetchSchedules().then(function(rules) {
      return rules;
    });

    return $q.all([promise1, promise2]).then(function(result) {
      let data = {
        shopHours: result[0].shopHours,
        appointments: result[0].appointments,
        rules: result[1],
        artist: vm.appointment.artist
      };
      calculateSchedule(
        data.rules,
        data.shopHours,
        data.appointments,
        data.artist)
      .then(function(schedule) {
        vm.schedule = schedule;
        let available = vm.schedule.available;
        let start = moment(vm.appointment.appointment_datetime);
        // Check to see if the artist is the same as before. If so, don't bother
        // adding the current appointment time to the available block of time.
        if (vm.appointmentCompare && vm.appointmentCompare.artist &&
        vm.appointmentCompare.artist.id === vm.appointment.artist.id &&
        start >= processedDates.start && vm.appointment.last_status &&
        vm.appointment.last_status.status_name !== 'Unconfirmed') {
          let end = moment(vm.appointment.end_date);
          available.push({
            start: start,
            end: end,
            start_time: start.format('HH:mm:ss'),
            end_time: end.format('HH:mm:ss'),
            length: end.diff(start, 'minutes')
          });
        }
        available = ScheduleUtilFactory.mergeConsecutive(available);
        vm.availability.blocks = ScheduleUtilFactory.filterByDuration(
          available,
          duration
        );
        vm.pagerStart = 0;
        vm.pagerEnd = vm.resultLimit;
        pagerStatus();
        vm.firstRun = false;
        vm.loading = false;
      });
    });
  }

  /**
   * @name calculateSchedule
   * @description description
   * @param {type} rules Description
   * @param {type} shopHours Description
   * @param {type} appointments Description
   * @param {type} artist Description
   * @return {type} Description
   */
  function calculateSchedule(rules, shopHours, appointments, artist) {
    return $q(function(resolve, reject) {
      let schedule = {
        artist: artist,
        schedules: [],
        appointments: [],
        artist_start_end_times: []
      };

      let start = shopHours.earliest.start;
      let end = shopHours.latest.end;
      let artistScheduleInstances =
      ScheduleUtilFactory.generateRuleInstances(rules, start, end);
      artistScheduleInstances =
      ScheduleUtilFactory.filterExceptions(artistScheduleInstances);
      //  Add schedule instances to the schedule object, grouped by artist.
      _.each(artistScheduleInstances, function(instance) {
        let artistSelected = instance.rule.artist;
        if (schedule.artist.id === artistSelected) {
          schedule.schedules.push(instance);
        }
      });

      for (let j = 0; j < appointments.length; j++) {
        let appointment = appointments[j];
        let status = appointment.last_status.status_name;
        let date = appointment.date;
        if (start <= date && date <= end &&
          (status === 'Confirmed' || status === 'Completed')) {
          schedule.appointments.push(appointment);
        }
      }
      let processedSchedule = processSchedule(schedule, shopHours);
      resolve(processedSchedule);
    });
  }

  /**
   * @name processSchedule
   * @description description
   * @param {type} scheduleGroup Description
   * @param {type} shopHours Description
   * @return {type} Description
   */
  function processSchedule(scheduleGroup, shopHours) {
    let start = shopHours.earliest.start;
    let end = shopHours.latest.end;

    let output = {
      artist: scheduleGroup.artist,
      artist_start_end_times: [],
      shop_start_time:
      start.diff(moment(start).startOf('day'), 'minutes'), // Not universally needed
      shop_end_time:
      end.diff(moment(end).startOf('day'), 'minutes'), // Not universally needed
      appointments: [],
      available: []
    };

    let appointments = scheduleGroup.appointments;
    if (appointments) {
      for (let i = 0; i < appointments.length; i++) {
        let appointment = appointments[i];
        let newBlock = {
          appointment_buffer: null, //  Do we still need this?
          start: appointment.date,
          end: appointment.end,
          start_time: appointment.date.format('HH:mm:ss'),
          end_time: appointment.end.format('HH:mm:ss'),
          interval_back_to_shop_start_time:
          appointment.date.diff(start, 'minutes'),
          length: appointment.duration, //  Should rename this to duration?
          appointment: appointment
        };
        output.appointments.push(newBlock);
      }
    }

    let scheduleInstances = scheduleGroup.schedules;
    if (scheduleInstances) {
      for (let x = 0; x < scheduleInstances.length; x++) {
        let instance = scheduleInstances[x];
        let rule = instance.rule;

        let interval = {
          start: instance.start,
          end: instance.end,
          start_time: rule.start_time,
          end_time: rule.end_time
        };
        output.artist_start_end_times.push(interval);
      }

      let available = CalculateAvailability.calculate(
        scheduleInstances,
        appointments
      );
      for (let d = 0; d < available.length; d++) {
        let item = available[d];
        item.start_time = item.start.format('HH:mm:ss');
        item.end_time = item.end.format('HH:mm:ss');
        item.length = item.end.diff(item.start, 'minutes'); //  Change to duration?
        item.interval_back_to_shop_start_time =
        item.start.diff(start, 'minutes');
      }
      output.available = available;
    }
    return output;
  }

  /**
  * @name getSchedule
  * @description Get the Schedule (after selecting the time block).
  * @param {unknown} block Unknown.
  */
  function getSchedule(block) {
    resetAlerts();
    let duration = ScheduleUtilFactory.calculateDuration(
      vm.appointment.durationHours,
      vm.appointment.durationMinutes
    );
    vm.selected.block = block;

    // Set Timepickers
    vm.appointment.appointment_start_time = vm.selected.block.start.toDate();
    vm.appointment.appointment_end_time =
    moment(vm.selected.block.start).add(duration, 'minutes').toDate();
  }

  /**
  * @name updateSearch
  * @description Start an avaibility search.
  */
  function updateSearch() {
    if (vm.appointment.artist.id && (vm.appointment.durationHours ||
    vm.appointment.durationMinutes)) {
      getAvailability();
    }
  }

  /**
  * @name detectCollision
  * @description Function to detect collisions with the current appointment and
  * other appointments on the schedule.
  */
  function detectCollision() {
    let available = vm.availability.blocks;
    let appointments = vm.schedule.appointments;
    let block = vm.selected.block;
    let appointment = {
      start: moment(vm.appointment.appointment_start_time),
      end: moment(vm.appointment.appointment_end_time),
      id: vm.appointment.id
    };
    CheckCollisions.check(available, appointments, block, appointment)
    .then(function(data) {
      vm.clientAlerts.errors = data.errors;
      vm.clientAlerts.warnings = data.warnings;

      // Use the properly verified Date.
      vm.appointment.adjusted_appointment_date = appointment.start;
    });
  }

  /**
  * @name selectArtist
  * @description Select an artist.
  * @param {unknown} artist Unknown.
  */
  function selectArtist(artist) {
    if (vm.appointment.artist && vm.appointment.artist.id === artist.id) {
      vm.appointment.artist = {};
      resetAvailabilityData();
    } else {
      vm.appointment.artist = artist;
      updateSearch();
    }
  }

  /**
  * @name updateTime
  * @description Update the start or end time to be a certain duration.
  * @param {sting} type Unknown.
  */
  function updateTime(type) {
    let update = vm.appointment;
    let duration = ScheduleUtilFactory
    .calculateDuration(update.durationHours, update.durationMinutes);
    if (type === 'start') {
      update.appointment_start_time = moment(update.appointment_end_time)
      .subtract(duration, 'minutes').toDate();
    } else {
      update.appointment_end_time = moment(update.appointment_start_time)
      .add(duration, 'minutes').toDate();
    }
    detectCollision();
  }

  /**
  * @name pagerPrev
  * @description Pager controls for the Previous button.
  */
  function pagerPrev() {
    vm.pagerStart -= vm.resultLimit;
    vm.pagerEnd -= vm.resultLimit;
    pagerStatus();
  }

  /**
  * @name pagerNext
  * @description Pager controls for the Next button.
  */
  function pagerNext() {
    vm.pagerStart += vm.resultLimit;
    vm.pagerEnd += vm.resultLimit;
    pagerStatus();
  }

  /**
  * @name processOverflow
  * @description Calculates the correct quantity for the last results in the pager.
  * @return {unknown} Unknown
  */
  function processOverflow() {
    return Math.max(vm.pagerEnd - vm.availability.blocks.length, 0);
  }

  /**
  * @name pagerStatus
  * @description Determine if the pager (Prev/Next) needs to be disabled or not.
  */
  function pagerStatus() {
    vm.prevDisabled = (vm.pagerStart < vm.resultLimit);
    vm.nextDisabled = (vm.pagerEnd >= vm.availability.blocks.length);
    vm.availability.pagedBlocks = vm.availability.blocks
    .slice(vm.pagerStart, vm.pagerEnd);
    vm.resultsOverflow = processOverflow();
    vm.selected = {};
  }

  /**
  * @name reset
  * @description Unknown.
  */
  function reset() {
    vm.firstRun = true;
    delete vm.selected_date;
    resetAlerts();
    resetAvailabilityData();
  }

  /**
  * @name resetAvailabilityData
  * @description Reset values to empty.
  */
  function resetAvailabilityData() {
    vm.availability = {};
  }

  /**
  * @name resetAlerts
  * @description Reset values to empty.
  */
  function resetAlerts() {
    vm.clientAlerts = {errors: [], warnings: []};
    // vm.serverAlerts = {errors: [], warnings: []};
  }
}

angular
.module('inkfinder')
.controller('AvailabilityController', AvailabilityController);



// WEBPACK FOOTER //
// ./components/schedule/availability/availability.controller.js